/**
 * R100 · Timezone helpers for attendance display.
 *
 * The backend stores every clock-in / clock-out as UTC ISO strings
 * (see routes_attendance.py — `datetime.now(timezone.utc).isoformat()`).
 * Frontends that sliced `iso.slice(11, 16)` were showing UTC (e.g.
 * "04:00") instead of local IST ("09:30").
 *
 * `wsTimezone()` reads the workspace tz once from the active membership;
 * falls back to the browser tz, then Asia/Kolkata. Pure — no React hook
 * needed so we can drop it into non-component helpers too.
 *
 * `fmtWorkspaceTime()` formats an ISO / Date for display in the resolved
 * workspace tz using 24-hour "HH:mm". `fmtWorkspaceDateTime()` includes
 * a short date. Both are Intl-based, so DST + non-half-hour offsets
 * (Iran, Nepal, Newfoundland) work automatically.
 */

const DEFAULT_TZ = "Asia/Kolkata";
let _cachedTz = null;

/** Resolve the workspace timezone. Optional override arg wins. */
export function wsTimezone(explicit) {
  if (explicit) return explicit;
  if (_cachedTz) return _cachedTz;
  try {
    const raw = localStorage.getItem("co_active_ws_tz");
    if (raw) { _cachedTz = raw; return raw; }
  } catch { /* ignore */ }
  try {
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return browserTz || DEFAULT_TZ;
  } catch { return DEFAULT_TZ; }
}

/** Called from the auth provider (or Settings save) so subsequent
 *  formatters use the freshly-loaded workspace tz. */
export function setWorkspaceTimezone(tz) {
  if (!tz) return;
  _cachedTz = tz;
  try { localStorage.setItem("co_active_ws_tz", tz); } catch { /* ignore */ }
}

/** Format an ISO string (or Date) as "HH:mm" in the workspace tz.
 *  Returns "" when input is falsy so callers can render "—" fallbacks. */
export function fmtWorkspaceTime(iso, tz) {
  if (!iso) return "";
  try {
    const d = typeof iso === "string" ? new Date(iso) : iso;
    if (Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit", minute: "2-digit", hour12: false,
      timeZone: wsTimezone(tz),
    }).format(d);
  } catch { return ""; }
}

/** Format as "Mon 3 Feb · 09:30". */
export function fmtWorkspaceDateTime(iso, tz) {
  if (!iso) return "";
  try {
    const d = typeof iso === "string" ? new Date(iso) : iso;
    if (Number.isNaN(d.getTime())) return "";
    const date = new Intl.DateTimeFormat("en-GB", {
      weekday: "short", day: "numeric", month: "short",
      timeZone: wsTimezone(tz),
    }).format(d);
    const time = fmtWorkspaceTime(d, tz);
    return `${date} · ${time}`;
  } catch { return ""; }
}
